import { isVisibleSkuEntity } from 'features/Sku/Items/SkuMenuData.normalizers';

export const RULES_BLOCK_MODE = {
  CREATE: 'create',
  EDIT: 'edit',
  READ_ONLY: 'readOnly',
};

export const NONE_VALUE = 'none';

export const defaultRulesFormValues = () => ({
  allowsMultiple: false,
  allowsQuantities: false,
  defaultOptionId: '',
  hasDefault: false,
  isRequired: false,
  maxSelection: NONE_VALUE,
  minSelection: NONE_VALUE,
});

const isNone = value => value === NONE_VALUE || value === '' || value == null;

export const selectionBounds = optionCount => {
  const count = Math.max(Number(optionCount) || 0, 0);
  return Array.from({ length: count }, (unused, index) => index + 1);
};

export const clampSelectionValue = (value, optionCount) => {
  if (isNone(value)) return NONE_VALUE;
  const numeric = Number(value);
  const max = Math.max(Number(optionCount) || 0, 1);
  if (numeric > max) return max;
  if (numeric < 1) return 1;
  return numeric;
};

// Maximum "None" means no cap, but selectionsMax must stay a positive integer, so it compiles
// to the current option count at save time; the server recomputes it when modifiers are later
// added or removed through the standalone modifier path.
export const compileRulesToGroupFields = (rulesValues, optionCount) => {
  const { allowsMultiple, allowsQuantities, isRequired, maxSelection, minSelection } = rulesValues;
  const requiredFloor = isRequired ? 1 : 0;

  if (!allowsMultiple) {
    return {
      allowsQuantities: !!allowsQuantities,
      selectionsMax: 1,
      selectionsMin: requiredFloor,
    };
  }

  return {
    allowsQuantities: !!allowsQuantities,
    selectionsMax: isNone(maxSelection) ? Math.max(Number(optionCount) || 0, 1) : Number(maxSelection),
    selectionsMin: isNone(minSelection) ? requiredFloor : Number(minSelection),
  };
};

export const applyDefaultOption = (options, defaultOptionId) =>
  options.map(option => ({ ...option, isDefault: option.id === defaultOptionId }));

// No-cap is stored as a max equal to the option count (see compileRulesToGroupFields), so a
// stored max at or above the count is indistinguishable from an explicit cap of that size and
// decodes back to "None". Lossless on re-compile.
export const rulesFormValuesFromGroup = (group, optionCount) => {
  const core = group?.core || {};
  // Deleting a modifier never clears its stored is_default, so a soft-deleted row can still
  // carry the flag and shadow the live default; only visible rows can seed the picker.
  const modifiers = (group?.modifiers || []).filter(isVisibleSkuEntity);
  const count = optionCount == null ? modifiers.length : optionCount;
  const selectionsMin = Number(core.selectionsMin) || 0;
  const selectionsMax = Number(core.selectionsMax) || 0;

  const allowsMultiple = selectionsMax !== 1;
  const defaultModifier = modifiers.find(modifier => modifier?.core?.isDefault);
  const defaultOptionId = defaultModifier?.core?.id || '';

  return {
    allowsMultiple,
    allowsQuantities: !!core.allowsQuantities,
    defaultOptionId,
    hasDefault: !!defaultOptionId,
    isRequired: selectionsMin >= 1,
    maxSelection: allowsMultiple && selectionsMax > 0 && selectionsMax < count ? selectionsMax : NONE_VALUE,
    minSelection: selectionsMin >= 1 ? selectionsMin : NONE_VALUE,
  };
};
